import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock3, Compass, Moon, RefreshCw, Sparkles, Sun, ArrowRight } from "lucide-react";
import { api } from "../../services/api";
import { PrimaryButton, SecondaryButton } from "./ui";
import { resolveAstriaLang } from "./copy";

// Astria Zodiac: the daily home card (backend astria-zodiac spec v1).
// One reading per user per local day from GET /backend/zodiac/today.

type ZodiacReading = {
  date: string;
  timezone: string;
  lang: string;
  theme: string;
  message: string;
  // Absent on cards saved before spec v1.1.
  energyLevel?: string;
  dailyEncouragement?: string;
  luckyWindow: string;
  luckyColor: string;
  luckyColorHex?: string;
  smallLuck: string;
  lifeGPSSignal: string;
  cta: { key: string; target: string };
};

// Card labels only; the reading itself already comes in the user's language.
const LABELS = {
  en: {
    title: "Astria Zodiac",
    todayEnergy: "Today's Energy",
    luckyWindow: "Lucky Window",
    luckyColor: "Lucky Color",
    smallLuck: "Small Luck",
    viewLifeGps: "View Life GPS",
    loading: "Reading today's energy…",
    error: "Could not load today's reading. Please try again.",
    retry: "Try again",
  },
  th: {
    title: "Astria Zodiac",
    todayEnergy: "พลังงานวันนี้",
    luckyWindow: "ช่วงเวลานำโชค",
    luckyColor: "สีนำโชค",
    smallLuck: "โชคเล็ก ๆ",
    viewLifeGps: "ดู Life GPS",
    loading: "กำลังอ่านพลังงานของวันนี้…",
    error: "โหลดข้อมูลวันนี้ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
    retry: "ลองอีกครั้ง",
  },
};

const CTA_ROUTES: Record<string, string> = { life_gps: "/lifeGps" };

const CSS = `
.zc-root{--zc-gold:var(--astria-accent-primary,#a78bff);--zc-star:#f3d9a4;--zc-t1:var(--astria-text-primary,#f7f3fa);--zc-t2:var(--astria-text-secondary,#c8bfd1);--zc-card:radial-gradient(120% 140% at 100% 0%,rgba(167,139,255,.24),transparent 55%),radial-gradient(90% 120% at 0% 100%,rgba(243,217,164,.08),transparent 60%),var(--astria-surface,#1a131f);--zc-border:rgba(167,139,255,.28);--zc-shadow:0 0 32px var(--astria-accent-glow,rgba(167,139,255,.25)),var(--astria-shadow-card,0 10px 40px -12px rgba(167,139,255,.45));--zc-tile:rgba(255,255,255,.05);--zc-tile-b:rgba(255,255,255,.09);--zc-chip:rgba(167,139,255,.16);--zc-skel:rgba(255,255,255,.08);--zc-swatch-b:rgba(255,255,255,.35)}
html[data-theme="light"] .zc-root{--zc-gold:var(--astria-accent-primary,#7c5ce0);--zc-star:#d9a24c;--zc-card:radial-gradient(120% 140% at 100% 0%,rgba(122,92,255,.13),transparent 55%),radial-gradient(90% 120% at 0% 100%,rgba(217,162,76,.08),transparent 60%),var(--astria-surface,#F2F2F7);--zc-border:rgba(124,92,224,.25);--zc-shadow:var(--astria-shadow-card,0 2px 8px rgba(26,26,31,.06));--zc-tile:rgba(26,26,31,.035);--zc-tile-b:rgba(26,26,31,.08);--zc-chip:rgba(124,92,224,.12);--zc-skel:rgba(26,26,31,.08);--zc-swatch-b:rgba(26,26,31,.2)}
.zc-t1{color:var(--zc-t1)}.zc-t2{color:var(--zc-t2)}.zc-gold{color:var(--zc-gold)}
.zc-tile{background:var(--zc-tile);border:1px solid var(--zc-tile-b);transition:transform .2s ease,border-color .2s ease,background .2s ease}
.zc-tile:hover{transform:translateY(-2px);border-color:var(--zc-border);background:var(--zc-chip)}
.zc-icon{display:inline-flex;height:1.75rem;width:1.75rem;align-items:center;justify-content:center;border-radius:9999px;background:var(--zc-chip)}
.zc-theme{background:linear-gradient(120deg,var(--zc-t1) 30%,var(--zc-gold) 100%);-webkit-background-clip:text;background-clip:text;color:transparent}
.zc-wheel{position:absolute;top:-70px;right:-70px;width:240px;height:240px;color:var(--zc-gold);opacity:.14;pointer-events:none;animation:zc-spin 90s linear infinite}
@keyframes zc-spin{to{transform:rotate(360deg)}}
.zc-skel{background:var(--zc-skel);border-radius:9999px}
@keyframes zc-pulse{0%,100%{opacity:.55}50%{opacity:1}}
@media (prefers-reduced-motion: reduce){.zc-anim,.zc-wheel{animation:none !important}}
`;

const Shell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section
    role="region"
    aria-label={label}
    className="zc-root relative overflow-hidden p-5 sm:p-7"
    style={{ background: "var(--zc-card)", border: "1px solid var(--zc-border)", boxShadow: "var(--zc-shadow)", borderRadius: "var(--astria-radius-card-premium, 1.5rem)" }}
  >
    <style>{CSS}</style>
    <svg className="zc-wheel" viewBox="0 0 100 100" fill="none" stroke="currentColor" aria-hidden="true">
      <circle cx="50" cy="50" r="47" strokeWidth=".6" />
      <circle cx="50" cy="50" r="36" strokeWidth=".4" />
      {Array.from({ length: 12 }, (_, i) => (
        <line key={i} x1="50" y1="3" x2="50" y2="14" strokeWidth=".6" transform={`rotate(${i * 30} 50 50)`} />
      ))}
    </svg>
    <div className="relative">{children}</div>
  </section>
);

const Header: React.FC<{ title: string; date?: string }> = ({ title, date }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      <span
        className="flex h-9 w-9 items-center justify-center rounded-full"
        style={{ background: "var(--zc-chip)", boxShadow: "0 0 14px var(--astria-accent-glow, rgba(167,139,255,.3))" }}
        aria-hidden="true"
      >
        <Moon size={16} className="zc-gold" strokeWidth={1.75} />
      </span>
      <span className="text-xs font-medium uppercase tracking-widest zc-t1">{title}</span>
    </div>
    {date && <span className="rounded-full px-3 py-1 text-xs zc-t2" style={{ background: "var(--zc-tile)", border: "1px solid var(--zc-tile-b)" }}>{date}</span>}
  </div>
);

const Tile: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon,
  label,
  children,
}) => (
  <div className="zc-tile rounded-2xl p-4">
    <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider zc-t2">
      <span className="zc-icon">{icon}</span>
      {label}
    </div>
    <div className="mt-3 text-sm leading-relaxed zc-t1">{children}</div>
  </div>
);

const Skeleton: React.FC<{ text: string }> = ({ text }) => (
  <div aria-busy="true" aria-live="polite">
    <div className="mt-5 space-y-3">
      <div className="zc-skel zc-anim h-6 w-2/5" style={{ animation: "zc-pulse 1.6s ease-in-out infinite" }} />
      <div className="zc-skel zc-anim h-3 w-full" style={{ animation: "zc-pulse 1.6s ease-in-out infinite .2s" }} />
      <div className="zc-skel zc-anim h-3 w-4/5" style={{ animation: "zc-pulse 1.6s ease-in-out infinite .4s" }} />
    </div>
    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="zc-tile zc-anim h-20 rounded-2xl"
          style={{ animation: `zc-pulse 1.6s ease-in-out infinite ${i * 0.2}s` }}
        />
      ))}
    </div>
    <p className="mt-4 flex items-center gap-2 text-sm zc-t2">
      <Sparkles size={14} className="zc-gold zc-anim" style={{ animation: "zc-pulse 1.6s ease-in-out infinite" }} />
      {text}
    </p>
  </div>
);

const formatDate = (dateKey: string, lang: string) => {
  try {
    return new Intl.DateTimeFormat(lang, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(
      new Date(`${dateKey}T00:00:00Z`),
    );
  } catch {
    return dateKey;
  }
};

const ZodiacCard: React.FC = () => {
  const navigate = useNavigate();
  const { t: tr, i18n } = useTranslation();
  const lang = resolveAstriaLang(i18n.language);
  const t = LABELS[lang as keyof typeof LABELS] || LABELS.en;

  const [reading, setReading] = useState<ZodiacReading | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback((isActive: () => boolean = () => true) => {
    setFailed(false);
    setReading(null);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    api
      .get("/backend/zodiac/today", { params: tz ? { tz } : {} })
      .then((r) => isActive() && setReading(r.data.data as ZodiacReading))
      .catch(() => isActive() && setFailed(true));
  }, []);

  useEffect(() => {
    let active = true;
    load(() => active);
    return () => {
      active = false;
    };
  }, [load]);

  if (failed) {
    return (
      <Shell label={t.title}>
        <Header title={t.title} />
        <p className="mt-5 text-sm zc-t2">{t.error}</p>
        <SecondaryButton className="mt-4 inline-flex items-center gap-2 text-sm" onClick={() => load()}>
          <RefreshCw size={14} />
          {t.retry}
        </SecondaryButton>
      </Shell>
    );
  }

  if (!reading) {
    return (
      <Shell label={t.title}>
        <Header title={t.title} />
        <Skeleton text={t.loading} />
      </Shell>
    );
  }

  const ctaRoute = CTA_ROUTES[reading.cta?.target] || "/lifeGps";

  return (
    <Shell label={t.title}>
      <Header title={t.title} date={formatDate(reading.date, lang)} />

      <div className="mt-6">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest zc-gold">
          <Sparkles size={12} style={{ color: "var(--zc-star)" }} />
          {t.todayEnergy}
        </div>
        <h2 className="zc-theme mt-2 text-3xl font-light tracking-tight sm:text-4xl">{reading.theme}</h2>
        <p className="mt-3 text-base leading-relaxed zc-t2">{reading.message}</p>
      </div>

      <div className={`mt-6 grid grid-cols-1 gap-3 ${reading.energyLevel ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
        {reading.energyLevel && (
          <Tile label={tr("chatInterface.energyLevel")} icon={<Sun size={14} className="zc-gold" />}>
            {reading.energyLevel}
          </Tile>
        )}
        <Tile label={t.luckyWindow} icon={<Clock3 size={14} className="zc-gold" />}>
          {reading.luckyWindow}
        </Tile>
        <Tile
          label={t.luckyColor}
          icon={
            <span
              className="inline-block h-3.5 w-3.5 rounded-full"
              style={{
                background: reading.luckyColorHex || "var(--zc-gold)",
                border: "1px solid var(--zc-swatch-b)",
                boxShadow: reading.luckyColorHex ? `0 0 10px ${reading.luckyColorHex}` : undefined,
              }}
              aria-hidden="true"
            />
          }
        >
          {reading.luckyColor}
        </Tile>
        <Tile label={t.smallLuck} icon={<Sparkles size={14} style={{ color: "var(--zc-star)" }} />}>
          {reading.smallLuck}
        </Tile>
      </div>

      {reading.dailyEncouragement && (
        <div className="mt-6">
          <div className="text-[11px] font-medium uppercase tracking-wider zc-t2">{tr("sidebar.HeadlineTitle")}</div>
          <p className="mt-2 text-base italic leading-relaxed zc-t1">“{reading.dailyEncouragement}”</p>
        </div>
      )}

      <div
        className="mt-6 flex items-start gap-3 rounded-2xl p-4"
        style={{ background: "var(--zc-chip)", border: "1px solid var(--zc-tile-b)" }}
      >
        <Compass size={18} className="zc-gold mt-0.5 flex-shrink-0" strokeWidth={1.75} />
        <p className="text-sm italic leading-relaxed zc-t2">{reading.lifeGPSSignal}</p>
      </div>

      <PrimaryButton
        className="mx-auto mt-6 flex items-center justify-center gap-2"
        onClick={() => navigate(ctaRoute)}
      >
        {t.viewLifeGps}
        <ArrowRight size={16} />
      </PrimaryButton>
    </Shell>
  );
};

export default ZodiacCard;
